import {
	Dialog,
	Form,
	FormItem,
	FormattedText,
	InputRow,
	Select,
	SelectItem,
	Toolbar,
	renderReactDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { DocumentItemViewModel, ListLocationModel } from "client/Api/Documents/SharePoint/common";
import {
	getShareWithUserInfo,
	findMembers,
	shareItem,
	SharedPermission,
	SharePointPeoplePickerItemModel,
	ShareWithUserInfo
} from "client/Api/Documents/SharePoint/sharePointSharing";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { PeoplePicker, attachToPeoplePicker } from "../PeoplePicker";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	convertToLocationModel
} from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	IRootValidation,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

export type InviteUserDialogProps = {
	document: DocumentItemViewModel;
	onClose: () => void;
};

export const InviteUserDialog = (props: InviteUserDialogProps): ReactElement => {
	const {
		document,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isShareByEmailEnabled, setIsShareByEmailEnabled] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		member: undefined,
		permission: SharedPermission.CanView
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const location = useMemo(
		(): ListLocationModel => {
			return convertToLocationModel(document.location);
		},
		[document.location]
	);

	useEffect(
		(): (() => void) => {
			const load = async (
				id: number,
				location: ListLocationModel,
				signal: AbortSignal
			): Promise<void> => {
				setIsBusy(true);
				try {
					const info: ShareWithUserInfo = await getShareWithUserInfo(
						location,
						id,
						signal
					);

					if (!info.canBeSharedByCurrentUser) {
						alertManager.showError(localize("_NoPermissionsToInviteUserMessage"));
						onClose();
						return;
					}

					setIsShareByEmailEnabled(info.isShareByEmailEnabled);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();

			load(
				document.id,
				location,
				cancellation.signal
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			document.id,
			location,
			onClose
		]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const member: SharePointPeoplePickerItemModel = ensureNotNil(formData.member);

				await shareItem(
					member,
					document,
					formData.permission,
					globalCancellation
				);

				alertManager.showSuccess((
					<FormattedText
						text={localize("_ItemSharedSuccessfully")}
						thingsToReplace={[
							{ what: "{0}", filler: document.name },
							{ what: "{1}", filler: member.name }
						]}
					/>
				));

				onClose();

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_YouCantChangePermissions"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			document,
			formData.member,
			formData.permission,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose
		]
	);

	const findMembersHandler = useCallback(
		(
			searchTerm: string,
			signal: AbortSignal
		): Promise<readonly SharePointPeoplePickerItemModel[]> => {

			return findMembers(
				location,
				searchTerm,
				isShareByEmailEnabled,
				signal
			);
		},
		[isShareByEmailEnabled, location]
	);

	const isSubmitButtonEnabled: boolean = isNotNil(formData.member);

	return (
		<Dialog
			trackingId="sp-invite-user-dialog"
			hasBusyIndicator={isBusy}
		>

			<Dialog.Header
				title={`${localize("_Share")} '${document.name}'`}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_InvitePeople")} isRequired>
						<InputRow>
							<InputRow.Stretch>
								<PeoplePicker
									{...attachToPeoplePicker(formHandler.for("member"))}
									onFindMembers={findMembersHandler}
									autoFocus="focus"
									areArbritraryEmailAddressesAllowed={isShareByEmailEnabled}
									menuTitle={localize("_InvitePeople")}
								/>
							</InputRow.Stretch>

							<NoUniqueInputIdContext>
								<Select
									{...formHandler.for("permission").attachToSelect()}
									options={permissionOptions}
									menuTitle={localize("_Permissions")}
								/>
							</NoUniqueInputIdContext>
						</InputRow>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="share"
						text={localize("_Share")}
						isDisabled={!isSubmitButtonEnabled}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="close"
						text={localize("_Close")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
InviteUserDialog.displayName = "InviteUserDialog";

type FormData = {
	member: SharePointPeoplePickerItemModel | undefined;
	permission: SharedPermission.CanView | SharedPermission.CanEdit;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forObject("member")
	.add(
		isNotNullNorUndefined()
	);

const permissionOptions: readonly SelectItem<SharedPermission.CanView | SharedPermission.CanEdit>[] = [
	{
		text: localize("_CanView"),
		value: SharedPermission.CanView
	},
	{
		text: localize("_CanEdit"),
		value: SharedPermission.CanEdit
	}
];

export function openInviteUserDialog(props: Omit<InviteUserDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<InviteUserDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
